import React, { useEffect, useState } from 'react';
import { fetchDailyPnLAPI } from '../../services/tradingApi';
import { X, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const PnLCalendarModal = ({ isOpen, onClose }) => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [currentMonthDate, setCurrentMonthDate] = useState(new Date());

  useEffect(() => {
    const loadData = async () => {
      if (!isOpen) return;
      setLoading(true);
      setError(null);
      try {
        const authHeaders = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        const response = await fetchDailyPnLAPI(currentMonthDate, authHeaders);
        setData(response.data);
      } catch (err) {
        console.error('Failed to load PnL data', err);
        setError('Failed to load calendar data.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [isOpen, token, currentMonthDate]);

  const handlePrevMonth = () => {
    const prev = new Date(currentMonthDate);
    prev.setMonth(prev.getMonth() - 1);
    setCurrentMonthDate(prev);
  };

  const handleNextMonth = () => {
    const next = new Date(currentMonthDate);
    next.setMonth(next.getMonth() + 1);
    setCurrentMonthDate(next);
  };

  if (!isOpen) return null;

  return (
    <div className="pnl-calendar-modal-overlay" onClick={onClose}>
      <div className="pnl-calendar-modal-content" onClick={e => e.stopPropagation()}>
        <button className="pnl-calendar-modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        {loading && !data ? (
          <div className="pnl-loading">
            {/*PnLCalendar loading message */}
            <RefreshCw className="spinner" size={32} />
            <p>Loading Calendar...</p>
          </div>
        ) : error && !data ? (
          <div className="pnl-error">{error}</div>
        ) : data ? (
          <div className={`pnl-calendar-container ${loading ? 'fetching' : ''}`}>
            {/* Header Section */}
            <div className="pnl-header-row">
              <div className="pnl-title-section">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <button onClick={handlePrevMonth} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                    <ChevronLeft size={24} />
                  </button>
                  <h2 style={{ margin: 0 }}>
                    {data.month.split(' ')[0]} <span className="landing-gold" style={{ fontWeight: 600 }}>{data.month.split(' ')[1]}</span>
                    {loading && <RefreshCw size={16} className="spinner" style={{ marginLeft: '12px' }} />}
                  </h2>
                  <button onClick={handleNextMonth} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                    <ChevronRight size={24} />
                  </button>
                </div>
                <div className="pnl-subtitle">— by {user?.name || 'User'}</div>
              </div>
              <div className="pnl-stats-right">
                <div className="pnl-stat-box">
                  <div className="pnl-stat-label">AVG DAILY P/L</div>
                  <div className={`pnl-stat-value ${data.avgDailyPnL >= 0 ? 'positive' : 'negative'}`}>
                    {data.avgDailyPnL >= 0 ? '+' : '-'}${Math.abs(data.avgDailyPnL).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>

            <div className="pnl-main-stats-row">
              <div className="pnl-net-profit">
                <div className="pnl-stat-label">NET PROFIT / LOSS</div>
                <div className={`pnl-net-value ${data.netProfitLoss >= 0 ? 'positive' : 'negative'}`}>
                  {data.netProfitLoss >= 0 ? '+' : '-'}${Math.abs(data.netProfitLoss).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="pnl-currency">USD</span>
                </div>
              </div>
              <div className="pnl-best-day">
                <div className="pnl-stat-label">BEST DAY</div>
                <div className="pnl-stat-value positive">
                  +${data.bestDay.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="pnl-best-day-date">{data.bestDay.date}</div>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="pnl-calendar-grid">
              {/* Day Headers */}
              <div className="pnl-day-header">SUN</div>
              <div className="pnl-day-header">MON</div>
              <div className="pnl-day-header">TUE</div>
              <div className="pnl-day-header">WED</div>
              <div className="pnl-day-header">THU</div>
              <div className="pnl-day-header">FRI</div>
              <div className="pnl-day-header">SAT</div>
              <div className="pnl-day-header week-header">WEEK</div>

              {/* Calendar Days */}
              {data.weeks.map((week, wIndex) => (
                <React.Fragment key={wIndex}>
                  {week.days.map((day, dIndex) => {
                    let className = 'pnl-day-cell';
                    if (day.month !== 'current') {
                      className += ' empty';
                    } else if (day.pnl > 0) {
                      className += ' positive-day';
                    } else if (day.pnl < 0) {
                      className += ' negative-day';
                    } else {
                      className += ' neutral-day';
                    }

                    return (
                      <div className={className} key={dIndex}>
                        <div className="pnl-date">{day.date}</div>
                        {day.month === 'current' && day.pnl !== 0 && (
                          <div className="pnl-amount">
                            {day.pnl > 0 ? '+' : '-'}${Math.abs(day.pnl) >= 1000 ? (Math.abs(day.pnl) / 1000).toFixed(2) + 'K' : Math.abs(day.pnl).toFixed(2)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Weekly Total */}
                  <div className={`pnl-week-cell ${week.total > 0 ? 'positive-week' : week.total < 0 ? 'negative-week' : 'neutral-week'}`}>
                    <div className="pnl-week-label">WEEK {week.weekNumber}</div>
                    <div className="pnl-week-amount">
                      {week.total >= 0 ? '+' : '-'}${Math.abs(week.total) >= 1000 ? (Math.abs(week.total) / 1000).toFixed(2) + 'K' : Math.abs(week.total).toFixed(2)}
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PnLCalendarModal;
